import React, { useEffect } from 'react'
import PropTypes from 'prop-types'
import { connect } from 'react-redux'
import dayjs from 'dayjs'
import { find } from 'lodash'

import { colors } from '@lmc/common/constants'
import { hexToRgbA } from '../../../../../common/utils'
import { getShowAlert } from '../EMARResidentStockScreen/utils'
import Operations from '../../../../../operations'
import Actions from '../../../../../actions'
import Selectors from '../../../../../selectors'
import { Banner, HelpLink } from '../../../../../components/generic'
import {
  MedStockBreadcrumb,
  MedStockChart,
  MedStockDateFilter,
  MedStockEmptyState,
  MedStockHeader,
  MedStockLogsTable,
} from './components'
import { withRouter } from '../../../../../withRouter'
import useQuery from '../../../../../hooks/useQuery'

// Main render function

export function EMARMedicationStockScreen(props) {
  const { fetchData, fetchLogs, logs, medication, router, testId } = props

  const query = useQuery()
  const isPDF = query.get('printpdf')
  const startDateQuery = query.get('startDate')
  const endDateQuery = query.get('endDate')

  const [startDate, setStartDate] = React.useState(
    isPDF && startDateQuery
      ? dayjs(startDateQuery)
      : dayjs().subtract(14, 'days'),
  )
  const [endDate, setEndDate] = React.useState(
    isPDF && endDateQuery ? dayjs(endDateQuery) : dayjs(),
  )

  const todayDate = dayjs()

  const residentId = router.params.id

  useEffect(() => {
    fetchData({
      medicationId: router.params.medication_id,
      residentId: router.params.id,
      startDate,
      endDate,
    })
    if (residentId) {
      fetchLogs({
        resident: residentId,
        medicationId: router.params.medication_id,
        startDate,
        endDate,
      })
    }
  }, [startDate, endDate, residentId])

  if (!medication || !logs) return null
  if (!logs.length)
    return (
      <MedStockEmptyState
        {...props}
        endDate={endDate}
        setEndDate={setEndDate}
        setStartDate={setStartDate}
        startDate={startDate}
        todayDate={todayDate}
        testId={testId}
      />
    )

  const showAlert = getShowAlert(medication)

  return (
    <div
      style={isPDF ? styles.pdfWrapper : styles.wrapper}
      data-testid={
        testId
          ? `${testId}-emar-medication-stock-screen`
          : 'emar-medication-stock-screen'
      }
    >
      <MedStockHeader
        {...props}
        isPDF={isPDF}
        startDate={startDate}
        endDate={endDate}
        testId={testId}
      />
      {showAlert && (
        <Banner
          color={colors.red}
          customStyles={styles.stockAlertBanner}
          text="The stock levels for this medication are low."
          data-testid={
            testId
              ? `${testId}-emar-stock-alert-banner`
              : 'emar-stock-alert-banner'
          }
        />
      )}
      {/* {!isPDF && <MedStockBreadcrumb {...props} />} */}
      {!isPDF && (
        <MedStockDateFilter
          endDate={endDate}
          setEndDate={setEndDate}
          setStartDate={setStartDate}
          startDate={startDate}
          todayDate={todayDate}
          testId={testId}
        />
      )}
      <MedStockChart
        medication={medication}
        startDate={startDate}
        endDate={endDate}
        logs={logs}
        testId={testId}
      />
      <MedStockLogsTable {...props} logs={logs} testId={testId} />
      {/* <div style={styles.helpLinkContainer}>
        <HelpLink supportLink="https://support.logmycare.co.uk/en/collections/2749236-emar" />
      </div> */}
    </div>
  )
}

EMARMedicationStockScreen.propTypes = {
  fetchData: PropTypes.func.isRequired,
  fetchLogs: PropTypes.func.isRequired,
  item: PropTypes.object.isRequired,
  location: PropTypes.object,
  logs: PropTypes.array.isRequired,
  medication: PropTypes.object.isRequired,
  openStockLogModal: PropTypes.func.isRequired,
  resident: PropTypes.object.isRequired,
  role: PropTypes.string.isRequired,
  router: PropTypes.object.isRequired,
  testId: PropTypes.string,
}

const styles = {
  helpLinkContainer: {
    marginTop: 24,
  },
  pdfWrapper: {
    position: 'absolute',
    top: 0,
    left: 0,
    width: 1190,
    minHeight: 1480,
    zIndex: 1000,
    background: 'white',
    padding: '24px 28px',
  },
  stockAlertBanner: {
    border: `1px solid ${hexToRgbA(colors.red, 0.5)}`,
    margin: '10px 0px',
  },
  wrapper: {
    padding: '24px 0px 48px',
    margin: '0px auto',
    maxWidth: 1080,
  },
}

const mapStateToProps = (state, props) => ({
  emarRoundTimings: state.data['emar-round-timings'],
  role: state.global.user.role,
  logs: Selectors.getMedicationLogs(state, {
    id: props.router.params.medication_id,
  }),
  item: find(state.data['log-category-items'], { itemId: 'emarStockCount' }),
  medication: find(state.data['emar-resident-medications'], {
    id: props.router.params.medication_id,
  }),
  resident: Selectors.getResidentProfile(state, {
    id: props.router.params.id,
  }),
})

const mapDispatchToProps = (dispatch) => ({
  fetchData: ({ medicationId, residentId, startDate, endDate }) => {
    dispatch(Operations.loadList('emar-round-timings'))
    dispatch(Operations.loadListItem('emar-resident-medications', medicationId))
    dispatch(
      Operations.loadList('emar-rounds', {
        resident: residentId,
        from: dayjs(startDate).startOf('day').toISOString(),
        to: dayjs(endDate).endOf('day').toISOString(),
      }),
    )
  },
  fetchLogs: ({ resident, medicationId, startDate, endDate }) =>
    dispatch(
      Operations.loadList('logs', {
        from: dayjs(startDate).startOf('day').toISOString(),
        to: dayjs(endDate).endOf('day').toISOString(),
        emarResidentMedication: medicationId,
        resident,
      }),
    ),
  openStockLogModal: ({ resident, medication, item, category }) => {
    // addStockLog
    dispatch(Actions.openModal({ modalId: 'addStockLog' }))
    dispatch(
      Actions.addForm({
        formId: 'addStockLog',
        initial: {
          type: 'emar',
          hidden: true,
          resident,
          emarResidentMedication: medication,
          title: 'Stock Count',
          item,
          category,
          timeLogged: new Date(),
        },
      }),
    )
  },
})

export default withRouter(
  connect(mapStateToProps, mapDispatchToProps)(EMARMedicationStockScreen),
)
